import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import {
  use911Address,
  useUpdate911Address,
  useRemove911Address,
  use911Alerts,
  useAdd911Alert,
  useRemove911Alert,
} from '../../hooks';
import { Shield, MapPin, Bell, TriangleAlert, Trash2, Plus, Info } from 'lucide-react';
import { US_STATES } from '@/lib/constants';

interface E911TabProps {
  tn: string;
}

export function E911Tab({ tn }: E911TabProps) {
  // Address state
  const { data: address, isLoading: addressLoading } = use911Address(tn);
  const update911Address = useUpdate911Address();
  const remove911Address = useRemove911Address();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const [form, setForm] = useState({
    callerName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    plusFour: '',
  });

  // Alerts state
  const { data: alerts, isLoading: alertsLoading } = use911Alerts(tn);
  const addAlert = useAdd911Alert();
  const removeAlert = useRemove911Alert();
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    if (address) {
      setForm({
        callerName: address.caller_name || '',
        address1: address.address?.address1 || '',
        address2: address.address?.address2 || '',
        city: address.address?.city || '',
        state: address.address?.state || '',
        zipCode: address.address?.zip_code || '',
        plusFour: address.address?.plus_four || '',
      });
    }
  }, [address]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressSubmit = () => {
    update911Address.mutate({ tn, ...form });
  };

  const handleRemove = () => {
    remove911Address.mutate(tn, {
      onSuccess: () => {
        setShowRemoveConfirm(false);
        setForm({
          callerName: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zipCode: '',
          plusFour: '',
        });
      },
    });
  };

  const handleAddAlert = () => {
    if (!newEmail.trim()) return;
    addAlert.mutate(
      { tn, email: newEmail.trim() },
      { onSuccess: () => setNewEmail('') }
    );
  };

  const handleRemoveAlert = (email: string) => {
    removeAlert.mutate({ tn, email });
  };

  const isRegistered = address && address.registered;
  const isLoading = addressLoading || alertsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Main Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-red-50 rounded-xl">
          <Shield className="h-6 w-6 text-red-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">E911 Settings</h3>
          <p className="text-sm text-slate-500 mt-1">
            Configure emergency address and notification settings for this DID.
          </p>
        </div>
      </div>

      {/* Section: 911 Address */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="h-4 w-4 text-slate-500" />
          <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
            Emergency Address
          </h4>
        </div>
        <div className="bg-slate-50 rounded-xl p-5 space-y-4">
          <Input
            label="Displayed Caller Name"
            value={form.callerName}
            onChange={(e) => handleChange('callerName', e.target.value)}
            placeholder="Name to display for 911 calls"
          />

          <Input
            label="Street Address"
            value={form.address1}
            onChange={(e) => handleChange('address1', e.target.value)}
            placeholder="Street address"
          />

          <Input
            label="Address Line 2"
            value={form.address2}
            onChange={(e) => handleChange('address2', e.target.value)}
            placeholder="Apt, Suite, etc. (optional)"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City"
              value={form.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="City"
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                State
              </label>
              <select
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:border-telvia-500 focus:ring-1 focus:ring-telvia-500"
              >
                <option value="">Select state</option>
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="ZIP Code"
              value={form.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value.replace(/\D/g, ''))}
              placeholder="12345"
              maxLength={5}
            />
            <Input
              label="ZIP +4"
              value={form.plusFour}
              onChange={(e) => handleChange('plusFour', e.target.value.replace(/\D/g, ''))}
              placeholder="6789"
              maxLength={4}
            />
          </div>

          <div className="flex justify-between pt-2">
            {isRegistered && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowRemoveConfirm(true)}
                disabled={update911Address.isPending || remove911Address.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove 911
              </Button>
            )}
            <div className={!isRegistered ? 'ml-auto' : ''}>
              <Button
                onClick={handleAddressSubmit}
                isLoading={update911Address.isPending}
                size="sm"
              >
                Save Address
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Section: 911 Alerts */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Bell className="h-4 w-4 text-slate-500" />
          <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
            911 Dial Notifications
          </h4>
        </div>
        <div className="bg-slate-50 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Receive email or SMS when 911 is dialed from this number.
            </p>
            <span className="text-xs text-slate-500">{alerts?.length || 0}/10 configured</span>
          </div>

          {alerts && alerts.length > 0 ? (
            <ul className="space-y-2">
              {alerts.map((email, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-slate-200"
                >
                  <span className="text-sm text-slate-700">{email}</span>
                  <button
                    onClick={() => handleRemoveAlert(email)}
                    disabled={removeAlert.isPending}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-4 text-slate-400">
              <Bell className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No alert recipients configured</p>
            </div>
          )}

          {/* Add New Alert */}
          <div className="pt-4 border-t border-slate-200">
            <div className="flex gap-2">
              <Input
                placeholder="email@example.com or 5551234567@txt.att.net"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddAlert()}
              />
              <Button
                onClick={handleAddAlert}
                isLoading={addAlert.isPending}
                disabled={!newEmail.trim() || (alerts?.length || 0) >= 10}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* SMS Gateway Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-2">SMS Gateway Domains</p>
              <p className="text-sm text-blue-800 mb-2">Send alerts to mobile devices via SMS:</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-blue-700">
                <div><strong>AT&T:</strong> number@txt.att.net</div>
                <div><strong>Verizon:</strong> number@vtext.com</div>
                <div><strong>T-Mobile:</strong> number@tmomail.net</div>
                <div><strong>Sprint:</strong> number@messaging.sprintpcs.com</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        title="Remove 911 Registration"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <TriangleAlert className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">
                This will remove the E911 registration
              </p>
              <p className="text-sm text-red-700 mt-1">
                Emergency services will no longer have location information for calls made from this number.
              </p>
            </div>
          </div>

          {address?.address && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Current address:</span>
              </p>
              <p className="text-sm text-slate-800 mt-1">
                {address.address.address1}
                {address.address.address2 && `, ${address.address.address2}`}
                <br />
                {address.address.city}, {address.address.state} {address.address.zip_code}
              </p>
            </div>
          )}

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowRemoveConfirm(false)}
              disabled={remove911Address.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRemove}
              isLoading={remove911Address.isPending}
            >
              Remove 911
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
}
